import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Car,
  Plane,
  Shield,
  Clock,
  Sparkles,
  MapPin,
  Phone,
  Star,
  ChevronRight,
  CheckCircle2,
  Users,
  Award,
  Timer,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const stagger = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  transition: { staggerChildren: 0.1 },
};

export const Home = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: Car,
      title: t.services.valet.title,
      description: t.services.valet.description,
      link: '/leistungen',
    },
    {
      icon: Plane,
      title: t.services.shuttle.title,
      description: t.services.shuttle.description,
      link: '/leistungen',
    },
    {
      icon: Sparkles,
      title: t.services.carcare.title,
      description: t.services.carcare.description,
      link: '/leistungen',
    },
  ];

  const benefits = [
    { icon: Timer, text: t.benefits.terminal },
    { icon: Shield, text: t.benefits.secure },
    { icon: Clock, text: t.benefits.service },
    { icon: CheckCircle2, text: t.benefits.cancel },
  ];

  const stats = [
    { number: '10+', label: t.stats.experience, icon: Award },
    { number: '500', label: t.stats.parkingSpots, icon: Car },
    { number: '99%', label: t.stats.satisfaction, icon: Star },
    { number: '20.000+', label: t.stats.parkedCars, icon: Users },
  ];

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2074&auto=format&fit=crop')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#050505]" />
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10 py-24 md:py-32">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 text-sm">
                <MapPin className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-white/80">{t.hero.badge}</span>
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6"
            >
              {t.hero.title1}{' '}
              <span className="text-[#D4AF37]">{t.hero.title2}</span>
              <br />
              {t.hero.title3}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-base md:text-lg lg:text-xl text-white/70 mb-8 max-w-2xl leading-relaxed"
            >
              {t.hero.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href="https://mcdgroup.de/buchungssystem/index.php"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-8 py-4 rounded-full font-semibold text-base hover:bg-[#E5C34A] transition-all duration-300 transform hover:-translate-y-1 gold-glow"
                data-testid="hero-book-btn"
              >
                {t.hero.cta}
                <ChevronRight className="w-5 h-5" />
              </a>
              <Link
                to="/leistungen"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/20 text-white px-8 py-4 rounded-full font-medium text-base hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
                data-testid="hero-services-btn"
              >
                {t.hero.services}
              </Link>
            </motion.div>

            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-white/70 text-sm"
                >
                  <benefit.icon className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                  <span>{benefit.text}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"
            />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]" data-testid="stats-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            {...stagger}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                {...fadeUp}
                viewport={{ once: true }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32" data-testid="services-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            {...fadeUp}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
              {t.services.title}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              {t.services.subtitle}
            </h2>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto">
              {t.services.description}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={service.link}
                  className="block h-full bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#D4AF37]/30 transition-all duration-500 group"
                  data-testid={`service-card-${index}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                    <service.icon className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#D4AF37] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-medium">
                    {t.services.learnMore}
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="why-us-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              {...fadeUp}
              viewport={{ once: true }}
            >
              <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
                {t.whyUs.subtitle}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                {t.whyUs.title1}{' '}
                <span className="text-[#D4AF37]">{t.whyUs.title2}</span>
              </h2>
              <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8">
                {t.whyUs.description}
              </p>

              <ul className="space-y-4 mb-8">
                {t.whyUs.features.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-white/80 text-sm">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://mcdgroup.de/buchungssystem/index.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-[#D4AF37] text-black px-8 py-4 rounded-full font-semibold hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
                  data-testid="why-us-book-btn"
                >
                  {t.whyUs.cta}
                </a>
                <a
                  href="tel:+4915126777707"
                  className="inline-flex items-center justify-center gap-2 text-white/70 hover:text-[#D4AF37] transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span>+49 151 26777707</span>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1506521781263-d8422e82f27a?q=80&w=2070&auto=format&fit=crop"
                  alt="Premium Parkservice"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-3">
                  <Star className="w-8 h-8 text-black" />
                  <div>
                    <div className="text-2xl font-bold text-black">4.9</div>
                    <div className="text-black/70 text-sm">{t.whyUs.rating}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            {...fadeUp}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1556388158-158ea5ccacbd?q=80&w=2070&auto=format&fit=crop')`,
              }}
            />
            <div className="absolute inset-0 bg-black/70" />
            
            <div className="relative z-10 px-8 py-16 md:px-16 md:py-24 text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                {t.cta.title1}{' '}
                <span className="text-[#D4AF37]">{t.cta.title2}</span>{' '}
                {t.cta.title3}
              </h2>
              <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto mb-8">
                {t.cta.description}
              </p>
              <a
                href="https://mcdgroup.de/buchungssystem/index.php"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-10 py-5 rounded-full font-semibold text-lg hover:bg-[#E5C34A] transition-all duration-300 transform hover:-translate-y-1 gold-glow"
                data-testid="cta-book-btn"
              >
                {t.cta.button}
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
